import {
  HEALTHY_SOLD_MARGIN,
  SOLD_STAGES,
  closingReport,
  computeLadder,
  formatMoney,
  isTestJob,
  jobMatches,
  scoreboard,
  soldWithinDateFilter,
  type ClosingReport,
  type DateFilter,
  type LadderTotals,
  type Scoreboard as SoldTotals,
} from "@roof-hawk/core";
import { useMemo, useState } from "react";

import { Figure, JobRows, ListHead, Scoreboard } from "../../components/ui";
import { t } from "../../i18n";
import { useAsync } from "../../lib/hooks";
import { fetchClosingTicks, fetchLadderInputs } from "../../lib/queries";
import type { AppData } from "../shell/Shell";

/** Every sold job, company-wide, with what it was all worth. */
export function MasterList({
  data,
  search,
}: {
  data: AppData;
  search: string;
}) {
  const [dateFilter, setDateFilter] = useState<DateFilter>("all");

  const rows = useMemo(
    () =>
      data.jobs
        // A practice job is not a sale. It is still in Prospects and Jobs,
        // badged; it is this list — the one the company reads its sold numbers
        // off — that it must stay out of.
        .filter((job) => !isTestJob(job))
        .filter((job) => SOLD_STAGES.includes(job.stage))
        // By the day it sold, not the day the lead came in: "This month" on
        // this list is what was sold this month.
        .filter((job) =>
          soldWithinDateFilter(job, dateFilter, data.approvals.get(job.id)),
        )
        .filter((job) => jobMatches(job, search)),
    [data.jobs, data.approvals, dateFilter, search],
  );

  /*
   * The money behind the rows on screen, and only those rows.
   *
   * Keyed on the ids so changing the date filter or typing in search re-adds
   * the jobs actually listed. A scoreboard that answers for a different set of
   * jobs than the table under it is worse than no scoreboard.
   */
  const ids = rows.map((job) => job.id);
  const key = ids.join(",");
  const ladders = useAsync(() => fetchLadderInputs(ids), [key]);

  const board = useMemo<SoldTotals | null>(() => {
    if (!ladders.data) return null;
    const totals: LadderTotals[] = rows.flatMap((job) =>
      (ladders.data!.get(job.id) ?? []).map((input) =>
        computeLadder({ ...input, jobType: job.job_type, place: job }),
      ),
    );
    return scoreboard(totals, rows.length);
  }, [rows, ladders.data]);

  /*
   * The closing percentage reads every job, not just the sold ones on the list
   * — its bottom half is the estimates that went out, sold or not. The date
   * filter picks jobs by when their estimate was sent, not when the lead came
   * in, so it is applied inside `closingReport` rather than with the rows.
   */
  const ticks = useAsync(fetchClosingTicks, []);
  const closing = useMemo<ClosingReport | null>(() => {
    if (!ticks.data) return null;
    const jobs = data.jobs
      .filter((job) => !isTestJob(job))
      .filter((job) => jobMatches(job, search));
    return closingReport(jobs, ticks.data, dateFilter);
  }, [data.jobs, ticks.data, dateFilter, search]);

  return (
    <>
      <ListHead
        title={t("Master — Sold Jobs")}
        count={rows.length}
        dateFilter={dateFilter}
        onDateFilter={setDateFilter}
      />
      <SoldScoreboard board={board} />
      <ClosingStrip report={closing} error={ticks.error} />
      <JobRows jobs={rows} values={data.values} />
    </>
  );
}

/**
 * Four figures across the top of the sold list.
 *
 * Revenue beside cost, because revenue on its own is the number that reads
 * best and tells you least. The margin carries the colour: it is the one that
 * says whether the month was worth having.
 */
function SoldScoreboard({ board }: { board: SoldTotals | null }) {
  // An empty strip while it loads, rather than zeros. A zero is a number, and a
  // number that is about to change is worse than nothing.
  if (!board) return <Scoreboard />;

  if (board.jobs === 0) {
    return (
      <Scoreboard>
        <span className="na-hint">
          {t("No sold jobs match the current filters.")}
        </span>
      </Scoreboard>
    );
  }

  const healthy = board.margin >= HEALTHY_SOLD_MARGIN;

  return (
    <Scoreboard>
      <Figure label={t("Revenue")} value={formatMoney(board.revenue)} />
      <Figure label={t("What it cost")} value={formatMoney(board.cost)} />
      <Figure
        label={t("Gross profit")}
        value={formatMoney(board.grossProfit)}
        tone={board.grossProfit < 0 ? "bad" : "good"}
      />
      <Figure
        label={t("Margin")}
        value={`${(board.margin * 100).toFixed(1)}%`}
        tone={healthy ? "good" : "bad"}
        note={
          healthy
            ? undefined
            : t("Under {pct}%", { pct: HEALTHY_SOLD_MARGIN * 100 })
        }
      />
      <Figure
        label={t("Average job")}
        value={formatMoney(board.averageJob)}
        tone="plain"
      />
      <Figure label={t("Jobs")} value={String(board.jobs)} tone="plain" />
    </Scoreboard>
  );
}

const percent = (rate: number) => `${Math.round(rate * 100)}%`;

/**
 * Closing percentage: estimates sent that went on to be signed or sent to
 * production. The company first, then one figure per rep.
 */
function ClosingStrip({
  report,
  error,
}: {
  report: ClosingReport | null;
  error: string | null;
}) {
  // A failed fetch looks like loading otherwise, and nobody is told.
  if (error) {
    return (
      <Scoreboard>
        <span className="na-hint">{t("Couldn't load closing %.")}</span>
      </Scoreboard>
    );
  }
  if (!report) return <Scoreboard />;

  if (report.company.sent === 0) {
    return (
      <Scoreboard>
        <span className="na-hint">
          {t("No estimates were sent in this period.")}
        </span>
      </Scoreboard>
    );
  }

  return (
    <Scoreboard>
      <Figure
        label={t("Closing %")}
        value={percent(report.company.rate)}
        note={t("{closed} of {sent} estimates sent", {
          closed: report.company.closed,
          sent: report.company.sent,
        })}
      />
      {report.reps.map((rep) => (
        <Figure
          key={rep.repId ?? "none"}
          label={rep.name}
          value={percent(rep.rate)}
          note={t("{closed} of {sent}", { closed: rep.closed, sent: rep.sent })}
        />
      ))}
    </Scoreboard>
  );
}
